import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/api';

function Task({ task, onToggle, onDelete }) {
  const priorityColors = {
    High: 'from-red-500/20 to-red-600/10 border-red-500/30 text-red-200',
    Medium: 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-200',
    Low: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-200'
  };

  return (
    <div className={`p-5 rounded-2xl mb-4 backdrop-blur-md border animate-in fade-in slide-in-from-bottom-4 duration-300 transition-all hover:scale-[1.01] bg-gradient-to-br ${task.status==='Completed' ? 'opacity-60 grayscale-[50%] border-white/10 bg-white/5' : priorityColors[task.priority] || priorityColors.Low}`}>
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-bold text-lg ${task.status==='Completed' ? 'line-through text-gray-400' : 'text-white'}`}>{task.title}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-tighter ${task.status==='Completed' ? 'bg-gray-500/20 text-gray-400' : 'bg-white/10 text-white/70'}`}>
              {task.priority}
            </span>
          </div>
          {task.description && <div className="text-sm text-gray-400 leading-relaxed">{task.description}</div>}
        </div>
        <div className="flex flex-col items-end gap-3 shrink-0">
          <button 
            onClick={()=>onToggle(task)} 
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${task.status==='Pending' ? 'bg-white text-black hover:bg-white/80' : 'bg-white/10 text-white hover:bg-white/20'}`}
          >
            {task.status==='Pending' ? 'Done' : 'Undo'}
          </button>
          <button 
            onClick={()=>onDelete(task)} 
            className="text-[10px] uppercase tracking-widest font-black text-red-400/70 hover:text-red-400 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard(){
  const { token, user, logout } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Low');
  const [error, setError] = useState('');

  const load = React.useCallback(async () => {
    try{
      const data = await apiRequest('/tasks', 'GET', null, token);
      setTasks(data);
    }catch(err){
      console.error(err);
    }
  }, [token]);

  useEffect(()=>{ load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if(!title.trim()) return;
    try{
      const newTask = await apiRequest('/tasks', 'POST', { title, description, priority }, token);
      setTasks(prev => [newTask, ...prev]);
      setTitle(''); setDescription('');
    }catch(err){
      setError(err.message || 'Could not add');
    }
  };

  const toggle = async (task) => {
    try{
      const updated = await apiRequest(`/tasks/${task.id}`, 'PUT', { status: task.status==='Pending' ? 'Completed' : 'Pending' }, token);
      setTasks(prev => prev.map(t => t.id===updated.id ? updated : t));
    }catch(err){
      console.error(err);
    }
  };

  const remove = async (task) => {
    try{
      await apiRequest(`/tasks/${task.id}`, 'DELETE', null, token);
      setTasks(prev => prev.filter(t => t.id !== task.id));
    }catch(err){
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8 text-white">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-10 backdrop-blur-xl bg-white/5 border border-white/10 p-5 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-tr from-blue-600 to-cyan-400 rounded-xl flex items-center justify-center font-black text-xl shadow-lg shadow-blue-500/20">T</div>
            <h1 className="text-2xl font-black tracking-tight">TaskMate</h1>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Logged in as</div>
              <div className="text-sm font-bold text-blue-400">{user?.name}</div>
            </div>
            <button onClick={logout} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all border border-red-500/20">
              Sign Out
            </button>
          </div>
        </div>

        {/* Add Task Panel */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 p-6 rounded-3xl mb-10 shadow-2xl">
          <form onSubmit={add}>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 space-y-4">
                <input 
                  required 
                  value={title} 
                  onChange={e=>setTitle(e.target.value)} 
                  placeholder="What needs to be done?" 
                  className="w-full bg-white/5 border border-white/10 p-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-medium placeholder-gray-500"
                />
                <textarea 
                  value={description} 
                  onChange={e=>setDescription(e.target.value)} 
                  placeholder="Add details (optional)..." 
                  className="w-full bg-white/5 border border-white/10 p-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-sm h-20 resize-none placeholder-gray-500"
                />
              </div>
              <div className="md:w-48 flex flex-col gap-4 justify-between font-bold uppercase tracking-widest">
                <div className="space-y-2">
                  <label className="text-[10px] text-gray-500 ml-1">Priority</label>
                  <select 
                    value={priority} 
                    onChange={e=>setPriority(e.target.value)} 
                    className="w-full bg-white/5 border border-white/10 p-3.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-xs"
                  >
                    <option className="bg-slate-900">Low</option>
                    <option className="bg-slate-900">Medium</option>
                    <option className="bg-slate-900">High</option>
                  </select>
                </div>
                <button className="w-full bg-gradient-to-tr from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white py-4 rounded-2xl font-black text-sm shadow-xl shadow-blue-500/20 transition-all transform hover:translate-y-[-2px] active:translate-y-[0]">
                  Create Task
                </button>
              </div>
            </div>
          </form>
        </div>

        {error && <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-2xl mb-8 text-center text-sm font-medium animate-bounce">{error}</div>}

        <div className="space-y-2">
          <div className="flex items-center gap-3 mb-6 ml-1">
            <div className="w-1.5 h-6 bg-blue-500 rounded-full"></div>
            <h2 className="text-xl font-black tracking-tight">Your Tasks</h2>
            <div className="flex-1 border-b border-white/5 ml-2"></div>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}</span>
          </div>
          
          {tasks.length===0 && (
            <div className="text-center py-20 backdrop-blur-sm bg-white/5 border border-dashed border-white/10 rounded-3xl">
              <div className="text-4xl mb-4 opacity-20">🎯</div>
              <div className="text-gray-500 font-bold uppercase tracking-widest text-xs">No active tasks found</div>
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-1">
            {tasks.map(t => <Task key={t.id} task={t} onToggle={toggle} onDelete={remove} />)}
          </div>
        </div>

      </div>
    </div>
  );
}
